import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Info, MousePointer2, Play, Video, X } from 'lucide-react';

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  findPastSession,
  isVideoSourceEligible,
  selectInteractionsKind,
  selectInteractionsSession,
  selectVideoKind,
  selectVideoSession,
  type EvergreenSourcesSelection,
} from '@/lib/evergreenSources';
import type { PastSession, WebinarForEvergreen } from '@/features/webinar/types';

type Props = {
  selection: EvergreenSourcesSelection;
  onChange: (selection: EvergreenSourcesSelection) => void;
  /** Webinaires proposés comme source, sans le webinaire configuré. */
  webinars: WebinarForEvergreen[];
  webinarsLoading: boolean;
  timezone: string;
  /** Sources figées pendant une session en cours. */
  locked: boolean;
  /** Raison affichée quand les sources sont figées. */
  lockedReason?: string | null;
  /** État des interactions enregistrées (import en cours, interactions hors vidéo). */
  interactionsNotice?: ReactNode;
  /** Présent quand le choix diffère de l'enregistré : revient aux sources enregistrées. */
  onReset?: (() => void) | null;
  /** Import du MP4 ou carte de la vidéo importée, affiché sous l'option MP4. */
  mp4Content: ReactNode;
  /** Carte de l'enregistrement du Live retenu, affichée sous son sélecteur. */
  liveVideoContent?: ReactNode;
  /** Identifiants de repli quand la session source n'est pas dans la liste des webinaires. */
  videoSessionIdentifier?: string | null;
  interactionsSessionIdentifier?: string | null;
};

function formatSessionDate(session: PastSession, timezone: string, locale: string): string {
  const isoDate = session.plannedDate ?? session.startedAt;
  if (!isoDate) return '-';
  const dateTime = DateTime.fromISO(isoDate, { zone: 'utc' }).setZone(timezone).setLocale(locale);
  const offsetHours = Math.floor(Math.abs(dateTime.offset) / 60);
  const offsetSign = dateTime.offset >= 0 ? '+' : '-';
  return `${dateTime.toFormat("EEEE d MMMM yyyy - HH:mm'h'")} (UTC${offsetSign}${offsetHours})`;
}

function LiveSessionPicker({
  webinars,
  sessionId,
  fallbackIdentifier,
  onSelect,
  eligible,
  disabled,
  loading,
  timezone,
}: {
  webinars: WebinarForEvergreen[];
  sessionId: number | null;
  fallbackIdentifier: string | null;
  onSelect: (sessionId: number | null) => void;
  eligible: (session: PastSession) => boolean;
  disabled: boolean;
  loading: boolean;
  timezone: string;
}) {
  const { t, i18n } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');

  if (sessionId != null) {
    const found = findPastSession(webinars, sessionId);
    return (
      <div className="flex items-center gap-3 rounded-lg border border-input bg-white p-2 pr-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-medium">
          <Play className="size-5 fill-current text-secondary" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-medium text-black">
            {found?.webinar.title ?? fallbackIdentifier ?? ''}
          </p>
          <p className="truncate text-xs text-secondary">
            {found ? formatSessionDate(found.session, timezone, i18n.language ?? 'fr') : ''}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="cursor-pointer"
            aria-label={t('sessionForm.deselectSession')}
          >
            <X className="size-6" />
          </button>
        )}
      </div>
    );
  }

  const groups = webinars
    .map((webinar) => ({ webinar, sessions: webinar.finishedSessions.filter(eligible) }))
    .filter((group) => group.sessions.length > 0);

  return (
    <Select
      value={undefined}
      onValueChange={(value) => onSelect(Number(value))}
      disabled={disabled}
    >
      <SelectTrigger label={t('sessionForm.selectPreviousWebinar')} className="h-14">
        <SelectValue placeholder={loading ? tCommon('loading') : undefined} />
      </SelectTrigger>
      <SelectContent>
        {groups.map(({ webinar, sessions }) => (
          <SelectGroup key={webinar.id}>
            <SelectLabel>{webinar.title}</SelectLabel>
            {sessions.map((session) => (
              <SelectItem key={session.id} value={String(session.id)}>
                {formatSessionDate(session, timezone, i18n.language ?? 'fr')}
                {session.transmissionMode === 'YOUTUBE' &&
                  ` · ${t('sessionForm.mp4.youtubeSessionTag')}`}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

function SourceOption({
  id,
  value,
  title,
  description,
  selected,
  disabled,
  children,
  className,
}: {
  id: string;
  value: string;
  title: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4 transition-colors',
        selected ? 'border-primary-300 bg-primary-50' : 'border-input bg-white',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <RadioGroupItem value={value} id={id} disabled={disabled} className="mt-1" />
        <label
          htmlFor={id}
          className={cn('flex flex-col gap-1', disabled ? 'cursor-default' : 'cursor-pointer')}
        >
          <span className="text-base font-medium text-black">{title}</span>
          <span className="text-sm text-tertiary">{description}</span>
        </label>
      </div>
      {selected && children && <div className="flex flex-col gap-3 sm:pl-7">{children}</div>}
    </div>
  );
}

function PanelHeader({
  id,
  icon,
  title,
  description,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      {icon}
      <div className="flex flex-col gap-1">
        <p id={id} className="text-lg font-semibold text-black">
          {title}
        </p>
        <p className="text-sm text-tertiary">{description}</p>
      </div>
    </div>
  );
}

/**
 * Vidéo et interactions de l'Evergreen, côte à côte. La grille partagée (subgrid) aligne les
 * options « À partir d'un ancien Live » des deux colonnes quelle que soit la longueur des en-têtes.
 */
export default function EvergreenSourcesPanel({
  selection,
  onChange,
  webinars,
  webinarsLoading,
  timezone,
  locked,
  lockedReason = null,
  interactionsNotice = null,
  onReset = null,
  mp4Content,
  liveVideoContent = null,
  videoSessionIdentifier = null,
  interactionsSessionIdentifier = null,
}: Props) {
  const { t } = useTranslation('createWebinar');
  const videoTitleId = useId();
  const interactionsTitleId = useId();
  const panelClassName =
    'flex flex-col gap-4 rounded-2xl border border-input bg-white p-4 lg:row-span-3 lg:grid lg:grid-rows-subgrid';

  return (
    <div className="flex flex-col gap-3">
      {locked && lockedReason && (
        <p className="flex items-start gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm text-tertiary">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
          <span>{lockedReason}</span>
        </p>
      )}
      {!locked && onReset && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onReset}
            className="cursor-pointer text-sm font-medium text-primary underline"
          >
            {t('sessionForm.sources.reset')}
          </button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2 lg:grid-rows-[auto_auto_auto]">
        <section className={panelClassName}>
          <PanelHeader
            id={videoTitleId}
            icon={<Video className="size-6 shrink-0" aria-hidden />}
            title={t('sessionForm.sources.video.title')}
            description={t('sessionForm.sources.video.description')}
          />
          <RadioGroup
            aria-labelledby={videoTitleId}
            className="contents"
            value={selection.video}
            onValueChange={(value) =>
              onChange(selectVideoKind(selection, value as EvergreenSourcesSelection['video']))
            }
            disabled={locked}
          >
            <SourceOption
              id="evergreen-video-live"
              value="LIVE"
              title={t('sessionForm.mp4.source.live')}
              description={t('sessionForm.sources.video.liveDescription')}
              selected={selection.video === 'LIVE'}
              disabled={locked}
            >
              <LiveSessionPicker
                webinars={webinars}
                sessionId={selection.videoSessionId}
                fallbackIdentifier={videoSessionIdentifier}
                onSelect={(sessionId) => onChange(selectVideoSession(selection, sessionId))}
                eligible={isVideoSourceEligible}
                disabled={locked}
                loading={webinarsLoading}
                timezone={timezone}
              />
              {liveVideoContent}
            </SourceOption>
            <SourceOption
              id="evergreen-video-mp4"
              value="MP4"
              title={t('sessionForm.mp4.source.mp4')}
              description={t('sessionForm.sources.video.mp4Description')}
              selected={selection.video === 'MP4'}
              disabled={locked}
              className="lg:self-start"
            >
              {mp4Content}
            </SourceOption>
          </RadioGroup>
        </section>

        <section className={panelClassName}>
          <PanelHeader
            id={interactionsTitleId}
            icon={<MousePointer2 className="size-6 shrink-0" aria-hidden />}
            title={t('sessionForm.sources.interactions.title')}
            description={t('sessionForm.sources.interactions.description')}
          />
          <RadioGroup
            aria-labelledby={interactionsTitleId}
            className="contents"
            value={selection.interactions}
            onValueChange={(value) =>
              onChange(
                selectInteractionsKind(
                  selection,
                  value as EvergreenSourcesSelection['interactions'],
                ),
              )
            }
            disabled={locked}
          >
            <SourceOption
              id="evergreen-interactions-live"
              value="LIVE"
              title={t('sessionForm.mp4.source.live')}
              description={t('sessionForm.sources.interactions.liveDescription')}
              selected={selection.interactions === 'LIVE'}
              disabled={locked}
            >
              <LiveSessionPicker
                webinars={webinars}
                sessionId={selection.interactionsSessionId}
                fallbackIdentifier={interactionsSessionIdentifier}
                onSelect={(sessionId) => onChange(selectInteractionsSession(selection, sessionId))}
                eligible={() => true}
                disabled={locked}
                loading={webinarsLoading}
                timezone={timezone}
              />
            </SourceOption>
            <div className="flex flex-col gap-4">
              <SourceOption
                id="evergreen-interactions-none"
                value="NONE"
                title={t('sessionForm.sources.interactions.none')}
                description={t('sessionForm.sources.interactions.noneDescription')}
                selected={selection.interactions === 'NONE'}
                disabled={locked}
              />
              <p className="flex items-start gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm text-tertiary">
                <Info className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span>{t('sessionForm.sources.interactions.info')}</span>
              </p>
              {interactionsNotice}
            </div>
          </RadioGroup>
        </section>
      </div>
    </div>
  );
}
